if (!customElements.get('store-locator-map')) {
  class StoreLocatorMap extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      const dataScript = this.querySelector('script[type="application/json"]');
      if (!dataScript) return;

      let config;
      try {
        config = JSON.parse(dataScript.textContent);
      } catch (e) {
        console.error('StoreLocatorMap: invalid JSON config', e);
        return;
      }

      const rawLocations = config.locations || [];
      const locations = rawLocations
        .map((loc) => ({ ...loc, lat: parseFloat(loc.lat), lng: parseFloat(loc.lng) }))
        .filter((loc) => Number.isFinite(loc.lat) && Number.isFinite(loc.lng));
      const skipped = rawLocations.length - locations.length;
      if (skipped > 0) {
        console.warn(`StoreLocatorMap: skipped ${skipped} of ${rawLocations.length} stores (missing or invalid lat/lon).`);
      }
      if (locations.length === 0) return;
      if (!config.apiKey) {
        console.warn('StoreLocatorMap: missing Google Maps API key (Theme settings → Google Maps).');
        return;
      }

      ((g) => {
        let h, a, k, p = 'The Google Maps JavaScript API', c = 'google', l = 'importLibrary', q = '__ib__', m = document, b = window;
        b = b[c] || (b[c] = {});
        const d = b.maps || (b.maps = {}), r = new Set(), e = new URLSearchParams();
        const u = () => h || (h = new Promise(async (f, n) => {
          await (a = m.createElement('script'));
          e.set('libraries', [...r] + '');
          for (k in g) e.set(k.replace(/[A-Z]/g, t => '_' + t[0].toLowerCase()), g[k]);
          e.set('callback', c + '.maps.' + q);
          a.src = `https://maps.${c}apis.com/maps/api/js?` + e;
          d[q] = f;
          a.onerror = () => (h = n(Error(p + ' could not load.')));
          a.nonce = m.querySelector('script[nonce]')?.nonce || '';
          m.head.append(a);
        }));
        d[l] ? console.warn(p + ' only loads once. Ignoring:', g) : (d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n)));
      })({ key: config.apiKey, v: 'weekly' });

      this._initMap(locations, config);
    }

    async _initMap(locations, config) {
      const [{ Map }, { AdvancedMarkerElement }] = await Promise.all([
        google.maps.importLibrary('maps'),
        google.maps.importLibrary('marker'),
      ]);

      const mapEl = this.querySelector('[data-locator-map]');
      if (!mapEl) return;

      const bounds = new google.maps.LatLngBounds();
      locations.forEach((loc) => bounds.extend({ lat: loc.lat, lng: loc.lng }));

      const map = new Map(mapEl, {
        mapId: config.mapId || 'DEMO_MAP_ID',
        center: { lat: locations[0].lat, lng: locations[0].lng },
        zoom: 5,
        mapTypeControl: false,
        streetViewControl: false,
      });

      if (locations.length > 1) {
        map.fitBounds(bounds, 48);
      }

      const styles = getComputedStyle(this);
      const tokens = {
        markerColor: styles.getPropertyValue('--store-locator-marker-color').trim() || '#3fa56a',
        markerLabel: styles.getPropertyValue('--store-locator-marker-label').trim() || '#ffffff',
        markerZ: parseInt(styles.getPropertyValue('--store-locator-marker-z'), 10) || 1000,
      };

      const markers = locations.map((loc) =>
        new AdvancedMarkerElement({
          position: { lat: loc.lat, lng: loc.lng },
          title: loc.title,
          content: buildPinElement(48, tokens),
          gmpClickable: true,
        })
      );

      const clusterer = (typeof markerClusterer !== 'undefined' && markerClusterer.MarkerClusterer)
        ? new markerClusterer.MarkerClusterer({ map, markers: [], renderer: buildClusterRenderer(tokens, AdvancedMarkerElement) })
        : null;
      if (clusterer) {
        clusterer.addMarkers(markers);
      } else {
        console.warn('StoreLocatorMap: marker clustering library not loaded; falling back to individual markers.');
        markers.forEach((m) => { m.map = map; });
      }

      const desktopList = this.querySelector('.store-locator__list');
      const mobileList = this.querySelector('.store-locator__list--mobile');
      const searchInput = this.querySelector('.store-locator__search-input');
      const searchButton = this.querySelector('.store-locator__search-button');
      const noResultsEls = this.querySelectorAll('.store-locator__no-results');

      const highlightItem = (idx) => {
        [desktopList, mobileList].forEach((list) => {
          if (!list) return;
          list.querySelectorAll('.store-locator__item.is-active').forEach((el) => el.classList.remove('is-active'));
          const next = list.querySelector(`.store-locator__item[data-loc-index="${idx}"]`);
          if (next) {
            next.classList.add('is-active');
            next.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }
        });
      };

      const mobileMql = window.matchMedia('(max-width: 989px)');
      const isMobile = () => mobileMql.matches;

      const scrollPageTo = (el, extraOffset = 0) => {
        const top = el.getBoundingClientRect().top + window.scrollY - 175 - extraOffset;
        window.scrollTo({ top, behavior: 'smooth' });
      };

      const selectStore = (idx, opts = {}) => {
        if (opts.panAndZoom && markers[idx]) {
          map.panTo(markers[idx].position);
          if (map.getZoom() < 10) map.setZoom(12);
        }
        if (opts.scrollToMap && isMobile()) {
          scrollPageTo(mapEl, -133);
        }
        highlightItem(idx);
      };

      markers.forEach((marker, idx) => {
        marker.addEventListener('gmp-click', () => selectStore(idx));
      });

      const matchesQuery = (loc, query) => {
        if (!query) return true;
        const haystack = `${loc.title || ''} ${loc.address || ''}`.toLowerCase();
        return haystack.indexOf(query) !== -1;
      };

      const applySearch = () => {
        const query = searchInput ? searchInput.value.trim().toLowerCase() : '';
        const visibleMarkers = [];
        let visibleCount = 0;
        locations.forEach((loc, i) => {
          const show = matchesQuery(loc, query);
          if (show) {
            visibleCount += 1;
            if (markers[i]) visibleMarkers.push(markers[i]);
          } else if (markers[i] && !clusterer) {
            markers[i].map = null;
          }
          [desktopList, mobileList].forEach((list) => {
            if (!list) return;
            const item = list.querySelector(`.store-locator__item[data-loc-index="${i}"]`);
            if (item) item.style.display = show ? '' : 'none';
          });
        });
        if (clusterer) {
          clusterer.clearMarkers();
          clusterer.addMarkers(visibleMarkers);
        } else {
          visibleMarkers.forEach((m) => { m.map = map; });
        }
        noResultsEls.forEach((el) => { el.hidden = visibleCount > 0; });
      };

      const activateItem = (target) => {
        if (target.closest('a')) return;
        const item = target.closest('.store-locator__item');
        if (!item) return;
        const idx = parseInt(item.getAttribute('data-loc-index'), 10);
        if (Number.isNaN(idx) || !markers[idx]) return;
        selectStore(idx, { panAndZoom: true, scrollToMap: true });
      };

      const onItemClick = (e) => activateItem(e.target);
      const onItemKeydown = (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        if (!e.target.closest('.store-locator__item')) return;
        e.preventDefault();
        activateItem(e.target);
      };
      const onSearchInput = () => applySearch();
      const onSearchKeydown = (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          applySearch();
        }
      };
      const onSearchButtonClick = () => {
        if (searchInput) searchInput.focus();
        applySearch();
        if (isMobile() && mobileList) scrollPageTo(mobileList);
      };

      this._cleanups = [];
      const bind = (el, type, handler) => {
        if (!el) return;
        el.addEventListener(type, handler);
        this._cleanups.push(() => el.removeEventListener(type, handler));
      };

      bind(desktopList, 'click', onItemClick);
      bind(desktopList, 'keydown', onItemKeydown);
      bind(mobileList, 'click', onItemClick);
      bind(mobileList, 'keydown', onItemKeydown);
      bind(searchInput, 'input', onSearchInput);
      bind(searchInput, 'keydown', onSearchKeydown);
      bind(searchButton, 'click', onSearchButtonClick);
    }

    disconnectedCallback() {
      if (this._cleanups) {
        this._cleanups.forEach((fn) => fn());
        this._cleanups = null;
      }
    }
  }

  function buildPinElement(size, tokens) {
    const half = size / 2;
    const innerR = half * 0.7;
    const wrap = document.createElement('div');
    wrap.className = 'store-locator__pin';
    wrap.style.cssText = `width:${size}px;height:${size}px;transform:translateY(50%);`;
    wrap.innerHTML =
      `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" style="display:block">` +
        `<style>` +
          `@keyframes slm-pulse{0%,100%{transform:scale(1);opacity:.45}50%{transform:scale(1.08);opacity:.65}}` +
          `.slm-halo{transform-origin:${half}px ${half}px;animation:slm-pulse 2s ease-in-out infinite}` +
        `</style>` +
        `<circle class="slm-halo" cx="${half}" cy="${half}" r="${half - 1}" fill="${tokens.markerColor}"/>` +
        `<circle cx="${half}" cy="${half}" r="${innerR}" fill="${tokens.markerColor}"/>` +
      `</svg>`;
    return wrap;
  }

  function buildClusterRenderer(tokens, AdvancedMarkerElement) {
    return {
      render: ({ count, position }) => {
        const size = 48;
        const half = size / 2;
        const wrap = document.createElement('div');
        wrap.className = 'store-locator__cluster';
        wrap.style.cssText = `position:relative;width:${size}px;height:${size}px;transform:translateY(50%);`;
        const label = document.createElement('span');
        label.style.cssText = `position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:${tokens.markerLabel};font-weight:700;font-size:13px;font-family:inherit;`;
        label.textContent = String(count);
        wrap.innerHTML =
          `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" style="display:block">` +
            `<circle cx="${half}" cy="${half}" r="${half}" fill="${tokens.markerColor}" fill-opacity="0.3"/>` +
            `<circle cx="${half}" cy="${half}" r="${half - 8}" fill="${tokens.markerColor}"/>` +
          `</svg>`;
        wrap.appendChild(label);
        const clusterMarker = new AdvancedMarkerElement({
          position,
          content: wrap,
          title: `${count} stores`,
          zIndex: tokens.markerZ + count,
          gmpClickable: true,
        });
        const origAddListener = clusterMarker.addListener.bind(clusterMarker);
        clusterMarker.addListener = (event, handler) =>
          origAddListener(event === 'click' ? 'gmp-click' : event, handler);
        return clusterMarker;
      },
    };
  }

  customElements.define('store-locator-map', StoreLocatorMap);
}
